import type { Sex, Title } from '../model/types.js';
import type { PlayerInfo } from '../tournament/types.js';

/** Parse CSV text (RFC 4180 quoting; comma, semicolon or tab separated). */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^\uFEFF/, '');
  const firstLine = src.split(/\r?\n/, 1)[0] ?? '';
  const counts = [',', ';', '\t'].map((d) => [d, firstLine.split(d).length - 1] as const);
  const delim = counts.reduce((a, b) => (b[1] > a[1] ? b : a))[0];
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < src.length; i++) {
    const c = src.charAt(i);
    if (inQuotes) {
      if (c === '"') {
        if (src.charAt(i + 1) === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
      continue;
    }
    if (c === '"') {
      inQuotes = true;
    } else if (c === delim) {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src.charAt(i + 1) === '\n') i++;
      row.push(field);
      field = '';
      if (row.some((f) => f.trim() !== '')) rows.push(row);
      row = [];
    } else {
      field += c;
    }
  }
  row.push(field);
  if (row.some((f) => f.trim() !== '')) rows.push(row);
  return rows;
}

const ALIASES: Record<string, keyof PlayerInfo> = {
  name: 'name',
  player: 'name',
  rating: 'rating',
  elo: 'rating',
  fide_rating: 'fideRating',
  fiderating: 'fideRating',
  national_rating: 'nationalRating',
  nationalrating: 'nationalRating',
  uscf_rating: 'nationalRating',
  title: 'title',
  federation: 'federation',
  fed: 'federation',
  country: 'federation',
  fide_id: 'fideId',
  fideid: 'fideId',
  id_fide: 'fideId',
  national_id: 'nationalId',
  nationalid: 'nationalId',
  uscf_id: 'nationalId',
  birth: 'birthDate',
  born: 'birthDate',
  birthdate: 'birthDate',
  birth_date: 'birthDate',
  birthday: 'birthDate',
  sex: 'sex',
  gender: 'sex',
  club: 'club',
  team: 'team',
  email: 'email',
  phone: 'phone',
};

const TITLES = new Set(['GM', 'IM', 'FM', 'CM', 'WGM', 'WIM', 'WFM', 'WCM', 'HM']);

/**
 * Read players from CSV with a header row. Recognised columns (any case):
 * name, rating/elo, fide_rating, national_rating, title, federation/fed,
 * fide_id, national_id, birth/born, sex/gender, club, team, email, phone.
 * "first"/"last" columns are joined as "Last, First".
 */
export function parseCsvPlayers(text: string): PlayerInfo[] {
  const rows = parseCsv(text);
  const header = (rows[0] ?? []).map((h) =>
    h
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_'),
  );
  const idx = (k: string): number => header.indexOf(k);
  const firstI = [idx('first'), idx('first_name'), idx('firstname'), idx('given')].find(
    (i) => i >= 0,
  );
  const lastI = [idx('last'), idx('last_name'), idx('lastname'), idx('surname')].find(
    (i) => i >= 0,
  );
  const out: PlayerInfo[] = [];
  for (const row of rows.slice(1)) {
    const rec: Record<string, string> = {};
    header.forEach((h, i) => {
      const key = ALIASES[h];
      if (key !== undefined) rec[key] = (row[i] ?? '').trim();
    });
    let name = rec['name'] ?? '';
    if (name === '' && lastI !== undefined) {
      const last = (row[lastI] ?? '').trim();
      const first = firstI !== undefined ? (row[firstI] ?? '').trim() : '';
      name = first === '' ? last : `${last}, ${first}`;
    }
    if (name === '') continue;
    const num = (k: string): number | undefined => {
      const v = rec[k];
      if (v === undefined || v === '') return undefined;
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    const fideRating = num('fideRating');
    const nationalRating = num('nationalRating');
    const rating = num('rating') ?? fideRating ?? nationalRating ?? 0;
    const titleRaw = (rec['title'] ?? '').toUpperCase();
    const sexRaw = (rec['sex'] ?? '').toLowerCase();
    const sex: Sex | null =
      sexRaw === 'm' || sexRaw === 'male'
        ? 'm'
        : sexRaw === 'f' || sexRaw === 'w' || sexRaw === 'female'
          ? 'f'
          : null;
    out.push({
      name,
      rating,
      ...(fideRating !== undefined ? { fideRating } : rating > 0 ? { fideRating: rating } : {}),
      ...(nationalRating !== undefined ? { nationalRating } : {}),
      title: TITLES.has(titleRaw) ? (titleRaw as Title) : '',
      federation: (rec['federation'] ?? '').toUpperCase(),
      fideId: rec['fideId'] ?? '',
      nationalId: rec['nationalId'] ?? '',
      birthDate: rec['birthDate'] ?? '',
      sex,
      club: rec['club'] ?? '',
      team: rec['team'] ?? '',
      email: rec['email'] ?? '',
      phone: rec['phone'] ?? '',
    });
  }
  return out;
}
